import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";

import { Action, Category, Label } from "constants/events";
import { useEventTracker } from "hooks/useEventTracker";
import useStrainsWithMenuItemAvailability from "hooks/useStrainsWithMenuItemAvailability";
import useSearch from "redux/hooks/useSearch";

import StrainCard, {
  StrainCardAccessoryTray,
} from "components/botanic/StrainCard";
import PhenotypeCarousel from "components/PhenotypeCarousel";

import StrainPlaceholderList from "../StrainList/StrainPlaceholderList";

const StrainResults: React.FC = () => {
  const ITEMS_PER_LOAD = 6;

  const {
    query: { q, searchCategory },
  } = useRouter();
  const { strain: strains, loadMoreSearchResults } = useSearch();

  const viewingCountOnLoad = searchCategory === "strain" ? 12 : 3;
  const [viewingCount, setViewingCount] = useState(viewingCountOnLoad);
  const [loading, setLoading] = useState(true);
  const { publishEvent } = useEventTracker();

  useEffect(() => {
    setViewingCount(viewingCountOnLoad);
    setLoading(false);
  }, [searchCategory]);

  const handleLoadMore = async () => {
    document.activeElement instanceof HTMLElement &&
      document.activeElement.blur();
    const newViewingCount = ITEMS_PER_LOAD + viewingCount;
    loadMoreSearchResults("strain", newViewingCount);
    publishEvent({
      action: Action.click,
      category: Category.showMoreStrainResultsButton,
      label: Label.showMoreStrainResultsButton,
    });

    setViewingCount(newViewingCount);
  };

  const { strainsWithMenuItemAvailability } =
    useStrainsWithMenuItemAvailability(strains);

  if (!strains || !strains.length) {
    return null;
  }

  return (
    <>
      <h2 className="heading--l">Strains</h2>
      <div className="font-normal mb-lg">matching "{q ? String(q) : ""}"</div>
      <div className="row mb-lg">
        {strains.slice(0, viewingCount).map((strain, i) => {
          const hasMenuItems = strainsWithMenuItemAvailability.find(
            (str) => str.name === strain.name
          )?.hasMenuItems;
          return loading ? (
            <StrainPlaceholderList count={1} key={strain.slug} />
          ) : (
            <div
              className="col-6 md:col-1/2 lg:col-1/3 mb-md"
              key={strain.slug}
            >
              <StrainCard
                orientation={{ desktop: "vertical", mobile: "horizontal" }}
                preserveHeight={{ desktop: true, mobile: false }}
                disableLazyLoad={i < 3}
                strain={strain}
                trackingLabel="view strain page"
                trackingCategory="strain_card"
                accessoryTray={
                  <StrainCardAccessoryTray
                    strain={strain}
                    hasMenuItems={hasMenuItems}
                    trackingCategory="strain_card"
                    trackingAction="click"
                    trackingLabel={
                      hasMenuItems
                        ? "shop menus button"
                        : "learn more button - view strain page"
                    }
                  />
                }
              />
            </div>
          );
        })}
      </div>
      {strains.length >= viewingCount && (
        <button
          className="button mb-xxl"
          onClick={handleLoadMore}
          data-testid="strain-results-load-more"
        >
          show more strain results
        </button>
      )}
      <span className="heading--m font-bold">Shop by strain type</span>
      <PhenotypeCarousel asLink className="mt-lg" />
    </>
  );
};

export default StrainResults;
